import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, X, FileSpreadsheet, Download, GlassWater, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { DrinkBadge, DRINK_LABELS } from "@/components/DrinkBadge";
import { useAuth } from "@/context/AuthContext";
import { ImportDialog } from "@/components/ImportDialog";
import { api, downloadFile, errMsg, openLabels } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LocationSelect } from "@/components/LocationSelect";
import { WineFormDialog } from "@/components/WineFormDialog";
import { CatalogResults } from "@/components/CatalogResults";
import { BottleChips } from "@/components/BottleChips";
import { LocationSummary } from "@/components/LocationSummary";
import { ClientSummary } from "@/components/ClientSummary";
import { BulkPlace } from "@/components/BulkPlace";
import { SelectionBar, toggleIn } from "@/components/SelectionBar";
import { BottleDetailsDialog } from "@/components/BottleDetailsDialog";

const emptyFilters = { q: "", vintage: "", producer: "", name: "", box_number: "", location_id: "", drink: "", pending: "" };

const FILTERS_KEY = "cellar_inventory_filters";
const savedFilters = () => {
  try {
    return { ...emptyFilters, ...JSON.parse(sessionStorage.getItem(FILTERS_KEY)) };
  } catch {
    return emptyFilters;
  }
};

const Stat = ({ label, value, id }) => (
  <div className="border-l border-[#2E252A] pl-5">
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E665D]">{label}</p>
    <p className="font-serif text-3xl text-[#F5F0EB] mt-1" data-testid={id}>{value ?? "–"}</p>
  </div>
);

const PendingStat = ({ value, active, onClick }) => (
  <button onClick={onClick} disabled={!value && !active} data-testid="stat-pending-button"
    className={`text-left border-l pl-5 transition-colors ${active ? "border-[#E5A93C]" : "border-[#2E252A] enabled:hover:border-[#E5A93C]"} disabled:cursor-default`}>
    <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${active ? "text-[#E5A93C]" : "text-[#6E665D]"}`}>Pending{active ? " · showing" : ""}</p>
    <p className={`font-serif text-3xl mt-1 ${active ? "text-[#E5A93C]" : "text-[#F5F0EB]"}`} data-testid="stat-pending">{value ?? "–"}</p>
  </button>
);

const FilterInput = ({ value, onChange, placeholder, id }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="bg-[#161214] h-10" data-testid={id} />
);

export default function Inventory() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(savedFilters);
  const [pickedWines, setPickedWines] = useState([]);
  const [wines, setWines] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [fromCatalog, setFromCatalog] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [picked, setPicked] = useState([]);
  const printPicked = () => openLabels(wines.flatMap((w) => (w.bottles || []).filter((b) => picked.includes(b.barcode)).map((b) => ({ barcode: b.barcode, title: w.name, sub: `${w.producer || ""} ${w.vintage || "NV"}`.trim(), wine: `${w.name} ${w.vintage || "NV"}`, extra: b.box_number ? `Box ${b.box_number}` : "" }))), navigate);
  const [locations, setLocations] = useState([]);
  const { user } = useAuth();
  const active = Object.values(filters).some(Boolean);
  const load = useCallback(async () => {
    const [w, s] = await Promise.all([
      active ? api.get("/wines", { params: { ...filters, with_bottles: true } }) : api.get("/locations"),
      api.get("/stats"),
    ]);
    if (active) setWines(w.data);
    else setLocations(w.data);
    setStats(s.data);
  }, [filters, active]);

  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    const t = setTimeout(() => load().catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [load, filters]);

  const isOwner = user.role === "owner";
  const deleteWines = async () => {
    if (!window.confirm(`Delete ${pickedWines.length} wine${pickedWines.length === 1 ? "" : "s"} and their bottles? You can restore them from Removed.`)) return;
    try {
      await api.post("/wines/delete", { ids: pickedWines });
      toast.success(`${pickedWines.length} wine${pickedWines.length === 1 ? "" : "s"} deleted`);
      setPickedWines([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const deleteBottles = async () => {
    const ids = wines.flatMap((w) => (w.bottles || []).filter((b) => picked.includes(b.barcode)).map((b) => b.id));
    if (!window.confirm(`Delete ${ids.length} bottle${ids.length === 1 ? "" : "s"}? You can restore them from Removed.`)) return;
    try {
      await api.post("/bottles/delete", { ids });
      toast.success(`${ids.length} bottle${ids.length === 1 ? "" : "s"} deleted`);
      setPicked([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const set = (k) => (v) => setFilters({ ...filters, [k]: v });
  const unplaced = useMemo(() => wines.flatMap((w) => (w.bottles || []).map((b) => ({ barcode: b.barcode, wine: `${w.vintage || "NV"} ${w.name}` }))), [wines]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 rise">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Back office</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB]">The cellar</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => downloadFile("/export/wines", `cellar-${new Date().toISOString().slice(0, 10)}.xlsx`)} className="h-11" data-testid="export-wines-button">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
          {user.role === "owner" && (
            <Button variant="outline" onClick={() => setImportOpen(true)} className="h-11" data-testid="import-wines-button">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Import Excel
            </Button>
          )}
          <Button onClick={() => { setFromCatalog(null); setOpen(true); }} className="bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB] h-11 px-6" data-testid="add-wine-button">
            <Plus className="w-4 h-4 mr-2" /> Add wine
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-6 mb-10 rise" style={{ animationDelay: "80ms" }}>
        <Stat label="Wines" value={stats?.wines} id="stat-wines" />
        <Stat label="Bottles" value={stats?.bottles} id="stat-bottles" />
        <PendingStat value={stats?.pending} active={filters.pending === "1"} onClick={() => set("pending")(filters.pending ? "" : "1")} />
        <Stat label="Boxes" value={stats?.boxes} id="stat-boxes" />
        <Stat label="Moves · 24h" value={stats?.moves_today} id="stat-moves" />
      </div>

      <DrinkAlert drink={stats?.drink} current={filters.drink} onPick={set("drink")} />

      <div className="rounded-xl border border-[#2E252A] bg-[#161214] p-4 sm:p-5 mb-6 rise" style={{ animationDelay: "140ms" }}>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E665D]" />
          <Input value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search anything — wine, producer, supplier, notes…" className="pl-10 h-11 bg-[#0D0A0B]" data-testid="inventory-search-input" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <FilterInput value={filters.vintage} onChange={set("vintage")} placeholder="Vintage" id="filter-vintage-input" />
          <FilterInput value={filters.producer} onChange={set("producer")} placeholder="Producer" id="filter-producer-input" />
          <FilterInput value={filters.name} onChange={set("name")} placeholder="Wine" id="filter-wine-input" />
          <FilterInput value={filters.box_number} onChange={set("box_number")} placeholder="Box number" id="filter-box-input" />
          <LocationSelect value={filters.location_id} onChange={set("location_id")} allowAll testId="filter-location-select" className="bg-[#161214] h-10" />
          <Button variant="ghost" disabled={!active} onClick={() => setFilters(emptyFilters)} className="h-10 text-[#A89F91]" data-testid="clear-filters-button">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      </div>

      {filters.location_id === "unassigned" && <BulkPlace bottles={unplaced} onDone={load} />}
      {active && <SelectionBar count={picked.length} onPrint={printPicked} onClear={() => setPicked([])} onDelete={isOwner ? deleteBottles : null} />}
      {active && isOwner && !!pickedWines.length && (
        <div className="flex items-center gap-3 mb-3" data-testid="wine-selection-bar">
          <span className="text-sm text-[#E57373]" data-testid="wine-selection-count">{pickedWines.length} wine{pickedWines.length === 1 ? "" : "s"} selected</span>
          <Button size="sm" variant="outline" onClick={deleteWines} className="text-[#E57373] border-[#8C2B36]/50" data-testid="delete-selected-wines-button"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete {pickedWines.length} wine{pickedWines.length === 1 ? "" : "s"}</Button>
          <Button size="sm" variant="ghost" onClick={() => setPickedWines([])} data-testid="clear-wine-selection-button"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
        </div>
      )}
      {active ? <WineTable wines={wines} onOpen={(id) => navigate(`/wines/${id}`)} showBottles onEditBottle={setEditing} picked={picked} onPick={toggleIn(setPicked)}
        pickedWines={isOwner ? pickedWines : null} onPickWine={(id) => setPickedWines((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
        onPickAll={() => setPickedWines(pickedWines.length === wines.length ? [] : wines.map((w) => w.id))} /> : <><ClientSummary /><LocationSummary locations={locations} unassigned={stats?.unassigned} onUnassigned={() => set("location_id")("unassigned")} /></>}
      {editing && <BottleDetailsDialog bottles={[editing]} onClose={() => setEditing(null)} onSaved={load} />}
      <div className="mt-6"><CatalogResults query={filters.q} onAdd={(c) => { setFromCatalog(c); setOpen(true); }} /></div>
      <WineFormDialog open={open} onOpenChange={setOpen} onSaved={load} catalog={fromCatalog} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}

const DrinkAlert = ({ drink, current, onPick }) => {
  if (!drink || !(drink.ready + drink.soon + drink.past)) return null;
  const items = [["past", drink.past], ["soon", drink.soon], ["ready", drink.ready]].filter(([, n]) => n);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E5A93C]/30 bg-[#E5A93C]/5 px-5 py-4 mb-6 rise" data-testid="drink-alert">
      <GlassWater className="w-5 h-5 text-[#E5A93C]" strokeWidth={1.5} />
      <span className="text-sm text-[#F5F0EB] mr-2">Drinking window</span>
      {items.map(([k, n]) => (
        <button key={k} onClick={() => onPick(current === k ? "" : k)} data-testid={`drink-alert-${k}`}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${current === k ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#4A3B43] text-[#A89F91] hover:text-[#F5F0EB]"}`}>
          {n} {n === 1 ? "wine" : "wines"} · {DRINK_LABELS[k].toLowerCase()}
        </button>
      ))}
    </div>
  );
};

const WineTable = ({ wines, onOpen, showBottles, onEditBottle, picked, onPick, pickedWines, onPickWine, onPickAll }) => (
  <div className="rounded-xl border border-[#2E252A] overflow-x-auto" data-testid="inventory-table">
    <table className="w-full text-sm">
      <thead className="bg-[#110D0F] text-left">
        <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E665D]">
          {pickedWines && <th className="pl-4 py-3 w-8"><Checkbox checked={!!wines.length && pickedWines.length === wines.length} onCheckedChange={onPickAll} data-testid="select-all-wines" /></th>}
          <th className="px-4 py-3 font-normal">Vintage</th>
          <th className="px-4 py-3 font-normal">Wine</th>
          <th className="px-4 py-3 font-normal hidden md:table-cell">Box</th>
          <th className="px-4 py-3 font-normal hidden md:table-cell">Location</th>
          <th className="px-4 py-3 font-normal text-right">Bottles</th>
        </tr>
      </thead>
      <tbody>
        {wines.map((w) => (
          <tr key={w.id} onClick={() => onOpen(w.id)} className="border-t border-[#2E252A] hover:bg-[#1E181B] cursor-pointer transition-colors" data-testid={`wine-row-${w.id}`}>
            {pickedWines && <td className="pl-4 py-4 w-8" onClick={(e) => e.stopPropagation()}><Checkbox checked={pickedWines.includes(w.id)} onCheckedChange={() => onPickWine(w.id)} data-testid={`select-wine-${w.id}`} /></td>}
            <td className="px-4 py-4 font-mono text-[#D4AF37]">{w.vintage || "NV"}</td>
            <td className="px-4 py-4">
              <p className="font-serif text-base text-[#F5F0EB]">{w.name}</p>
              <p className="text-xs text-[#A89F91]">{w.producer}{w.region ? ` · ${w.region}` : ""}</p>
              {w.drink_status && <div className="mt-1.5"><DrinkBadge status={w.drink_status} testId={`drink-badge-${w.id}`} /></div>}
              {showBottles && !!w.bottles?.length && <BottleChips bottles={w.bottles} onEdit={onEditBottle} selected={picked} onToggle={onPick} />}
            </td>
            <td className="px-4 py-4 hidden md:table-cell font-mono text-xs text-[#A89F91]">{w.box_numbers.join(", ") || "—"}</td>
            <td className="px-4 py-4 hidden md:table-cell text-xs text-[#A89F91]">{w.locations.join(", ") || "—"}</td>
            <td className="px-4 py-4 text-right font-mono text-[#F5F0EB]">
              {w.bottle_count}
              {!!w.pending_count && <p className="text-[10px] text-[#E5A93C]" data-testid={`pending-count-${w.id}`}>+{w.pending_count} pending</p>}
            </td>
          </tr>
        ))}
        {!wines.length && (
          <tr><td colSpan={6} className="px-4 py-16 text-center text-[#6E665D]" data-testid="inventory-empty">No wine matches these filters.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);
